import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { InquiryGuidelines } from '@/components/InquiryGuidelines';
import { Button, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { segmentClient } from '@/util/analytics';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';

const FIELD_LABEL = 'Message*';

const HELP_TEXT = 'View Guidelines';

const PLACEHOLDER_TEXT =
  'Do not include any patient identifiable information (PHI) in this message or attachments.';

interface MessageFieldProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  setValue: UseFormSetValue<InquiryDraftFormData>;
  isDisabled: boolean;
  previewExperts?: ExpertProfile[];
  /**
   * From "Link to inquiry": there's already a specific reason this inquiry is
   * going out, so the per-expert composer shows at a single expert too (normally
   * it needs 2+), and each expert's default opening line explains that reason
   * instead of the generic greeting.
   */
  isReferenceInquiry?: boolean;
}

const getExpertGreetingTarget = (expert: ExpertProfile) =>
  expert.lastName || expert.nameWithoutDoctor || expert.name;

const getExpertGreeting = (expert: ExpertProfile) => `Hi Dr. ${getExpertGreetingTarget(expert)}`;

const getReferenceInquiryGreeting = (expert: ExpertProfile) =>
  `Hi Dr. ${getExpertGreetingTarget(expert)}, I hope you are having a great day. One of our oncologists submitted this inquiry for you. Have a great day!`;

const getExpertDisplayName = (expert: ExpertProfile) =>
  expert.lastName ? `Dr. ${expert.lastName}` : expert.name;

/**
 * Unified message composition component.
 *
 * When multiple experts are selected, shows a tabbed interface where each tab
 * reveals an editable opening line for that expert, visually integrated above
 * the shared message textarea. Single-expert mode shows just the textarea,
 * unless `isReferenceInquiry` is set (see prop doc).
 *
 * Generic-greeting hint: only when more than one expert is selected and the
 * per-expert composer is not shown (e.g. multi-expert notes feature off).
 */
export function MessageField({
  control,
  errors,
  setValue,
  isDisabled,
  previewExperts = [],
  isReferenceInquiry = false,
}: MessageFieldProps) {
  const [isFocused, setIsFocused] = useState(false);

  const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);

  const [activeExpertId, setActiveExpertId] = useState<string | null>(null);

  const trackedCustomizedExpertIdsRef = useRef<Set<string>>(new Set());

  const expertNotes = useWatch({ control, name: 'expertNotes' });

  const expertIds = useWatch({ control, name: 'expertIds' });

  const hasMultipleExpertsSelected = (expertIds?.length ?? 0) > 1;

  const hasMultipleExperts = previewExperts.length > 1;

  /** Reference inquiries show the per-expert composer starting at 1 expert, not 2. */
  const hasComposerExperts = isReferenceInquiry ? previewExperts.length >= 1 : hasMultipleExperts;

  const buildGreeting = isReferenceInquiry ? getReferenceInquiryGreeting : getExpertGreeting;

  useEffect(() => {
    if (activeExpertId && !previewExperts.some((expert) => expert.id === activeExpertId)) {
      setActiveExpertId(null);
    }
  }, [activeExpertId, previewExperts]);

  useEffect(() => {
    if (!activeExpertId && hasComposerExperts) {
      setActiveExpertId(previewExperts[0]?.id ?? null);
    }
  }, [activeExpertId, hasComposerExperts, previewExperts]);

  const activeExpert = useMemo(
    () => previewExperts.find((expert) => expert.id === activeExpertId) ?? previewExperts[0],
    [activeExpertId, previewExperts]
  );

  useEffect(() => {
    if (!hasComposerExperts) {
      return;
    }

    const missingExperts = previewExperts.filter(
      (expert) => expertNotes?.[expert.id] === undefined
    );

    if (missingExperts.length === 0) {
      return;
    }

    const nextNotes: Record<string, string> = { ...(expertNotes ?? {}) };

    for (const expert of missingExperts) {
      nextNotes[expert.id] = buildGreeting(expert);
    }

    setValue('expertNotes', nextNotes, { shouldDirty: false });
  }, [buildGreeting, expertNotes, hasComposerExperts, previewExperts, setValue]);

  useEffect(() => {
    if (!expertNotes || Object.keys(expertNotes).length === 0) {
      return;
    }

    if (!hasComposerExperts) {
      setValue('expertNotes', {}, { shouldDirty: true });

      return;
    }

    const allowedIds = new Set(previewExperts.map((expert) => expert.id));

    const nextNotes = Object.fromEntries(
      Object.entries(expertNotes ?? {}).filter(([expertId]) => allowedIds.has(expertId))
    );

    const hasRemovedNotes = Object.keys(nextNotes).length !== Object.keys(expertNotes ?? {}).length;

    if (hasRemovedNotes) {
      setValue('expertNotes', nextNotes, { shouldDirty: true });
    }
  }, [expertNotes, hasComposerExperts, previewExperts, setValue]);

  const showComposer = hasComposerExperts && Boolean(activeExpert);

  const trackExpertMessageCustomized = (expert: ExpertProfile, value: string) => {
    const expertId = expert.id;

    if (trackedCustomizedExpertIdsRef.current.has(expertId)) {
      return;
    }

    const defaultGreeting = buildGreeting(expert);

    if (value.trim() === '' || value.trim() === defaultGreeting) {
      return;
    }

    trackedCustomizedExpertIdsRef.current.add(expertId);

    const trackPromise = segmentClient?.track?.(ANALYTICS_EVENTS.EXPERT_MESSAGE_CUSTOMIZED, {
      source: 'draft_inquiry_message',
      expertId,
      expertName: expert.name,
      selectedExpertCount: previewExperts.length,
    });

    trackPromise?.catch(() => undefined);
  };

  return (
    <>
      <Controller
        name="message"
        control={control}
        render={({ field: { onChange, onBlur, value }, formState }) => {
          const hasErrorMessage = !!errors.message?.message && formState.isSubmitted;

          const hasValue = typeof value === 'string' && value.trim().length > 0;

          const shouldShowError = hasErrorMessage && !hasValue;

          return (
            <FormControl isInvalid={shouldShowError} isDisabled={isDisabled}>
              <FormControlLabel className="mb-2">
                <HStack className="items-center justify-between gap-1.5">
                  <FormControlLabelText
                    size="sm"
                    className="font-semibold leading-normal text-typography-950">
                    {FIELD_LABEL}
                  </FormControlLabelText>
                  <Button
                    onPress={() => {
                      const trackPromise = segmentClient?.track?.(
                        ANALYTICS_EVENTS.INQUIRY_GUIDELINES_OPENED,
                        {
                          source: 'draft_inquiry_message',
                        }
                      );

                      trackPromise?.catch(() => undefined);

                      setIsGuidelinesOpen(true);
                    }}
                    variant="link"
                    size="sm"
                    action="secondary">
                    <ButtonText className="text-md text-info-700 underline data-[hover=true]:text-info-800">
                      {HELP_TEXT}
                    </ButtonText>
                  </Button>
                </HStack>
              </FormControlLabel>

              {showComposer ? (
                <VStack className="rounded-xl border border-outline-200 bg-background-0">
                  <VStack className="gap-2.5 p-4">
                    {previewExperts.length > 1 && (
                      <VStack className="overflow-hidden rounded-2xl border border-outline-100 bg-background-50 web:self-start">
                        <ScrollView
                          horizontal
                          nestedScrollEnabled
                          showsHorizontalScrollIndicator={false}
                          keyboardShouldPersistTaps="handled"
                          contentContainerClassName="min-h-9 flex-row flex-nowrap items-center gap-0.5 px-0.5 py-0.5">
                          {previewExperts.map((expert) => {
                            const isActive = expert.id === activeExpert.id;

                            return (
                              <Pressable
                                key={expert.id}
                                className={twJoin(
                                  'min-w-[4.5rem] max-w-[10.5rem] shrink-0 flex-row items-center rounded-xl px-3 py-1.5 web:max-w-[13rem]',
                                  isActive
                                    ? 'border border-outline-300 bg-background-0 shadow-sm'
                                    : 'bg-transparent data-[hover=true]:bg-background-100 data-[active=true]:bg-background-200'
                                )}
                                onPress={() => setActiveExpertId(expert.id)}
                                accessibilityLabel={expert.name}>
                                <Text
                                  size="xs"
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                  className={twJoin(
                                    'min-w-0 shrink',
                                    isActive
                                      ? 'font-medium text-typography-900'
                                      : 'font-normal text-typography-600'
                                  )}>
                                  {getExpertDisplayName(expert)}
                                </Text>
                              </Pressable>
                            );
                          })}
                        </ScrollView>
                      </VStack>
                    )}

                    <Controller
                      key={activeExpert.id}
                      name={`expertNotes.${activeExpert.id}` as const}
                      control={control}
                      render={({ field: noteField }) => (
                        <VStack className="gap-1.5">
                          <Text size="xs" className="font-medium text-typography-600">
                            Greeting for {getExpertDisplayName(activeExpert)}
                          </Text>
                          <Input
                            size="md"
                            variant="outline"
                            className="h-12 rounded-xl border-outline-200 bg-background-0">
                            <InputField
                              className="px-3 py-2 text-base placeholder:text-typography-500"
                              value={noteField.value ?? ''}
                              onChangeText={(nextValue) => {
                                noteField.onChange(nextValue);

                                trackExpertMessageCustomized(activeExpert, nextValue);
                              }}
                              onBlur={() => noteField.onBlur()}
                              editable={!isDisabled}
                              placeholder={buildGreeting(activeExpert)}
                            />
                          </Input>
                        </VStack>
                      )}
                    />
                  </VStack>

                  <VStack className="gap-1.5 border-t border-outline-200 px-4 pb-3 pt-3">
                    <Text size="xs" className="font-medium text-typography-600">
                      Question for all experts
                    </Text>
                    <Textarea className="h-[200px] border-0 focus:border-0 focus:ring-0 data-[focus=true]:border-0">
                      <TextareaInput
                        value={value}
                        onChangeText={onChange}
                        onBlur={() => onBlur()}
                        placeholder={PLACEHOLDER_TEXT}
                        multiline
                        textAlignVertical="top"
                        testID="inquiry-message-field"
                      />
                    </Textarea>
                  </VStack>
                </VStack>
              ) : (
                <Textarea className="h-[234px]">
                  <TextareaInput
                    value={value}
                    onChangeText={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                      onBlur();

                      setIsFocused(false);
                    }}
                    placeholder={PLACEHOLDER_TEXT}
                    multiline
                    textAlignVertical="top"
                    testID="inquiry-message-field"
                  />
                </Textarea>
              )}

              <Text
                className={twJoin(
                  'overflow-hidden text-xs text-typography-600 transition-all duration-200 ease-in-out',
                  isFocused && !showComposer && hasMultipleExpertsSelected
                    ? 'mt-2 max-h-20 opacity-100'
                    : 'mt-0 max-h-0 opacity-0'
                )}>
                Use a generic greeting if you plan to message more than one expert
              </Text>

              <FormControlError className="mt-2">
                <FormControlErrorText size="xs">{errors.message?.message}</FormControlErrorText>
              </FormControlError>
            </FormControl>
          );
        }}
      />

      <InquiryGuidelines isOpen={isGuidelinesOpen} onClose={() => setIsGuidelinesOpen(false)} />
    </>
  );
}
